import { message } from '@fuxi/eevee-ui';
import { map, uniq, pick, keys, values } from 'lodash-es';
import dayjs from 'dayjs';
import { FilterValue } from 'antd/es/table/interface';
import _copy from 'copy-to-clipboard';
import { Env } from '@yaotai/yaotai-tracker';
import { SubPackageSlugEnum, isSafari } from '@yaotai/common-utils';
import { pathToRegexp } from 'path-to-regexp';
import {
  CloudGamingResourceStatus,
  RehearsalResources,
  DeAgendaDynamicFieldValue,
  SwitchConfItem,
  ActivityDetailSlugEnum,
} from '../constants';
import { getSpecificActivityLoginPath } from './login';

export const checkSafariIsVersionLow = () => {
  const version = navigator.userAgent.toLowerCase().match(/version\/([\d.]+)/)?.[1];
  return isSafari && parseInt(version!) < 14;
};

export const guid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export function copy(content: string, successText?: string) {
  const flag = _copy(content);
  if (flag) message.success(successText || '已复制', 1);
}

export const getEncodedImgUrl = (url: string) => {
  return `url("${decodeURI(encodeURI(url))}")`;
};

export const getUrl = (url?: string) => {
  if (!url) return '';
  return /http/.test(url) ? url : `https://${url}`;
};

export function transformFilterValue({
  originalFilterArray = [],
  sourceFilterArray,
  transformKey = 'filterValue',
}: {
  originalFilterArray: FilterValue | null;
  sourceFilterArray: any[];
  transformKey?: string;
}) {
  return map(originalFilterArray || [], filterValue => {
    return sourceFilterArray.find(_item => _item.value === filterValue)?.[transformKey];
  });
}

export function findCurrentAvatar(
  role_id: number,
  roleAvatarList: {
    img: string;
    value: number;
  }[],
) {
  return roleAvatarList.find(item => item.value === role_id)?.img;
}

export const getFileNameByUrl = (url: string) => {
  return decodeURI(url.split('?')[0].split('/').slice(-1)[0]);
};

export const getFileTypeByUrl = (url?: string) => {
  return url?.split('?')[0].split('.').slice(-1)[0].toLowerCase() || '';
};

export const getMatchedKeyValue = <
  T extends {
    [key: string]: any;
    value: boolean | string | number;
  } = any,
>({
  array,
  matchValue,
  matchKey = 'value',
  findKey,
}: {
  array: T[];
  findKey: keyof T;
  matchKey?: keyof T;
  matchValue?: T[keyof T];
}) => {
  return array.find(item => item[matchKey] === matchValue)?.[findKey];
};

export const checkArrayDuplicate = (array: (string | number)[]) => {
  return uniq(array).length !== array.length;
};

export const valueDefaultDisplay = (value: string | number, defaultValue = '-') => {
  return value || defaultValue;
};

export function openActivityExclusive(aid: string) {
  window.open(`/${SubPackageSlugEnum.Center}/activity/${aid}`);
}

// eslint-disable-next-line react-func/max-lines-per-function
export const formatAgendas = (agendas: DeAgendaDynamicFieldValue[]) => {
  return [
    ...new Set(
      (agendas?.map(item =>
        dayjs(item?.time?.[0])
          .startOf('days')
          .valueOf(),
      ) as number[]) || [],
    ),
  ]
    ?.sort((a, b) => a - b)
    ?.map(i => ({
      dateRangeNode: i,
      agendasNode: agendas
        ?.filter(
          a =>
            dayjs(a?.time?.[0])
              ?.startOf('days')
              .valueOf() === i,
        )
        .map(ite => ({
          ...ite,
          diffDays:
            dayjs(ite?.time[1]).diff(dayjs(ite?.time[0])) >
            dayjs(ite?.time[0])
              .endOf('day')
              .diff(dayjs(ite?.time[0]))
              ? 1 + dayjs(ite?.time[1]).diff(dayjs(ite?.time[0]).endOf('day'), 'day')
              : 0,
        }))
        ?.sort((a, b) => dayjs(a?.time?.[0])?.valueOf() - dayjs(b?.time?.[0])?.valueOf()),
    }));
};

export const autoProtocolHref = (href?: string) => {
  if (!href) return '';
  if (href.includes('http://') || href.includes('https://')) {
    return href;
  } else {
    return `https://${href}`;
  }
};

export const getByteSize = (str: string = '') => {
  const len = str?.length || 0;
  let bytes = len;
  for (let index = 0; index < len; index++) {
    if (str.charCodeAt(index) > 255) {
      bytes = bytes + 1;
    }
  }
  return bytes;
};

export const getUtf8ByteSize = (inputStr: string = '') => {
  let totalLength = 0;
  /* 计算 utf-8 编码情况下的字符串长度 */
  for (let i = 0; i < (inputStr?.length || 0); i++) {
    if (inputStr.charCodeAt(i) <= parseInt('0x7F')) {
      totalLength += 1;
    } else if (inputStr.charCodeAt(i) <= parseInt('0x7FF')) {
      totalLength += 2;
    } else if (inputStr.charCodeAt(i) <= parseInt('0xFFFF')) {
      totalLength += 3;
    } else if (inputStr.charCodeAt(i) <= parseInt('0x1FFFFF')) {
      totalLength += 4;
    } else if (inputStr.charCodeAt(i) <= parseInt('0x3FFFFFF')) {
      totalLength += 5;
    } else {
      totalLength += 6;
    }
  }
  return totalLength;
};

// 国际电话格式format
export const formatPhone = (code: string | number, phone: string) => `+${code}${phone}`;

export const px2vw = (n: number) => {
  return `${(n / 750) * 100}vw`;
};

// px 转 vw 再转换到 px
export const px2vwThenPx = (n: number) => {
  const vw = (n / 750) * 100000;
  const px = (vw * window.innerWidth) / 100000;
  return px;
};
// 云游戏是否开放标志：主活动开启云游戏 或 彩排中有一个开启云游戏且资源拉起
export const getCloudResourceFlag = (activityFlag: boolean, rehearsals: RehearsalResources[] = []) => {
  return (
    activityFlag ||
    rehearsals?.some(item => item.cloud_gaming_flag && item.cloud_gaming_resource === CloudGamingResourceStatus.IN_USE)
  );
};

export const pickEnumToArr = <T>(enumType: object, pickArr: T[], pickType: 'key' | 'value' = 'value') => {
  const pickEnumObject = pick(enumType, pickArr as []);
  return pickType === 'key' ? keys(pickEnumObject) : values(pickEnumObject);
};
export function checkIsProd() {
  return window.location.origin === 'https://yaotai.163.com';
}

export function toHrefWithSearch(href: string) {
  return `${href}${window.location.search || ''}`;
}

// 返回编码字符串

export const encodeNextUrl = (str: string) => {
  if (!str) return '';
  const decodeUrl = decodeURIComponent(str);
  return decodeUrl === str ? encodeURIComponent(str) : str;
};

// 从开关配置列表中返回需要的状态
export const getStatusFromSwitchConfList = (type: number, switchConf: SwitchConfItem[]) => {
  const result = switchConf?.find(switchConfItem => switchConfItem.type === type);
  return result?.status;
};

export const getActivityConfigUrl = ({ sid, aid }: { aid: string; sid?: string }) => {
  return `/${SubPackageSlugEnum.Reception}${sid ? `/${sid}` : ''}/config/${aid}/${ActivityDetailSlugEnum.DETAIL}`;
};

export const getTrackEnv = (env: string) => {
  if (env.startsWith('dev') || env.startsWith('uat')) {
    return Env.Dev;
  }
  if (env === 'prod' || env.startsWith('udm')) {
    return Env.Prod;
  }
  if (env === 'qa') {
    return Env.Fat;
  }
  return Env.Fat;
};

export const getCustomUploadUrl = ({ baseUrl, gateWayUrl }: { baseUrl: string; gateWayUrl?: string }) => {
  return `${gateWayUrl ? gateWayUrl : ''}${baseUrl}`;
};

export function getLastTwoLevelsDomain(origin: string = window.location.hostname) {
  const regex = /[^.]+\.[^.]+$/; // 匹配最后两个层级的域名
  const matches = origin.match(regex); // 使用正则表达式匹配域名
  if (matches) {
    return matches[0];
  } else {
    return ''; // 如果匹配失败，返回 null 或其他适当的值
  }
}

export function getThirdLevelDomain(origin: string = window.location.hostname) {
  const regex = /[^.]+\.[^.]+\.[^.]+$/; // 匹配最后三个层级的域名
  const matches = origin.match(regex); // 使用正则表达式匹配域名
  if (matches) {
    return matches[0];
  } else {
    return ''; // 如果匹配失败，返回 null 或其他适当的值
  }
}

export function getYaotaiRbacCookieDomain(origin: string = window.location.hostname) {
  if (origin.includes('danlu.netease.com')) {
    return getThirdLevelDomain(origin);
  } else {
    return getLastTwoLevelsDomain(origin);
  }
}

export function getUrsCookieDomain(origin: string = window.location.hostname) {
  if (origin.includes('.netease.com')) {
    return 'netease.com';
  } else if (origin.includes('.163.com')) {
    return '163.com';
  } else {
    return 'netease.com';
  }
}

export const getCenterCurrentRedirectLoginUrl = () => {
  const centerActivityRegexp = pathToRegexp(`/${SubPackageSlugEnum.Center}/activity/:aid/(.*)`);
  const centerActivityRegexpEndWithAid = pathToRegexp(`/${SubPackageSlugEnum.Center}/activity/:aid(.*)`);

  const centerPcActivityMatch = centerActivityRegexp.exec(window.location.pathname); // 链接包含 aid 但不是以 aid 结尾的情况
  const centerPcActivityEndWithAidMatch = centerActivityRegexpEndWithAid.exec(window.location.pathname); // 链接以 aid 结尾后面没有其他 slug 的情况

  let aid = '';

  if (centerPcActivityMatch) {
    aid = centerPcActivityMatch[1];
  }

  if (!centerPcActivityMatch && centerPcActivityEndWithAidMatch) {
    aid = centerPcActivityEndWithAidMatch[1];
  }

  let redirectUrl = '';

  if (aid) {
    redirectUrl = `${getSpecificActivityLoginPath(aid)}?next=${window.location.href}`;
  } else {
    // 在权限校验失败的时候默认跳转到登录路由
    redirectUrl = `${window.origin}/${SubPackageSlugEnum.Center}/login`;
  }

  return redirectUrl;
};
